/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input} from '@angular/core';

import {ApiService} from '../api';
import {isValidPhotoUrl} from '../utils/strings';

/**
 * Component that displays a user avatar. When the user has a personalized picture, it is displayed (in a circle),
 * otherwise a circle filled with a solid color is displayed, on top of which the user's initials are presented.
 *
 * Usage :
 * ```
 * <gp-user-avatar [user]="user" size="lg"></gp-user-avatar>
 * ```
 *
 * Parameters :
 * - ```user``` mandatory, the user object for which to display the avatar
 * - ```size``` the size to display the avatar at. One of "lg" (96px wide) or "sm" (32px wide). Default is "sm".
 *
 */
@Component({
  selector: 'gp-user-avatar',
  template: `
    <span *ngIf="hasValidPicture" class="circle avatar {{classes}}"
          [ngClass]="{'w-96': size == 'lg', 'w-32': size == 'sm', 'w-64': size == 'md'}">
      <img [attr.src]="api.apiHost + pictureUri" (error)="onImageError()">
    </span>
    <span *ngIf="!hasValidPicture && initials" class="initials-circle {{classes}}"
          [ngClass]="{'initials-lg': size == 'lg', 'initials-md': size == 'md', 'initials-sm': size == 'sm'}">
      {{ initials }}
    </span>
    <span *ngIf="!hasValidPicture && !initials" class="initials-circle {{classes}}"
          [ngClass]="{'initials-lg': size == 'lg', 'initials-md': size == 'md', 'initials-sm': size == 'sm'}">
      <span [class.fa-user]="!backup"
            [class.fa-user-group]="backup"
            [ngClass]="{'fa-2x': size == 'lg', 'fa-light': size == 'sm', 'fa-1x': size == 'md'}"
            class="fa fa-fw"></span>
    </span>
  `,
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserAvatarComponent {

  @Input()
  public set pictureUri(value: string) {
    this._pictureUri = value;
    // A new picture URL should get a fresh chance to load.
    this.imageFailed = false;
  }

  public get pictureUri(): string {
    return this._pictureUri;
  }

  @Input()
  public initials: string;

  @Input()
  public backup: boolean;

  @Input()
  public size = 'sm';

  @Input()
  public classes: string;

  private _pictureUri: string;
  private imageFailed = false;

  constructor(public api: ApiService, private changeDetectorRef: ChangeDetectorRef) {
  }

  public get hasValidPicture(): boolean {
    return !this.imageFailed && isValidPhotoUrl(this.pictureUri);
  }

  public onImageError(): void {
    // The photo URL was valid but failed to load (404, expired link, network
    // error...): fall back to the initials / icon avatar.
    this.imageFailed = true;
    this.changeDetectorRef.markForCheck();
  }
}
